import React, { useRef } from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Link from '@material-ui/core/Link'
import makeStyles from '@material-ui/core/styles/makeStyles'
import {
  checkRegistrationWindow,
  formatDateRange,
  longMonthDayFormat,
} from 'lib/family-cup-utils/event-dates'
import { MY_PGA_COM_HOST } from 'env'
import RegisterWithAccessCode from 'components/program-marketing/registration-card/register-with-access-code'
import {
  OfflinePayment,
  Label,
  Value,
  useFitCardToScreen,
} from 'components/program-marketing/registration-card'
import clsx from 'clsx'
import { Cost } from './cost'

const useStyles = makeStyles(theme => ({
  grayBox: {
    background: '#EBEBEB',
    borderRadius: '5px',
    padding: '16px',
    textAlign: 'center',
  },
  card: {
    border: '1px solid #DBDCDD',
    boxSizing: 'border-box',
    boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.12)',
    borderRadius: '10px',
    [theme.breakpoints.up('md')]: {
      top: theme.spacing(12),
      position: 'sticky',
    },
  },
  smallScreenCard: {
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
    },
  },
}))

const getRegistrationTitle = (ended, registrationOpen) => {
  if (ended) return 'Event Concluded'
  if (registrationOpen) {
    return 'Registration Period'
  }
  return 'Registration Closes'
}

const RegistrationClosed = ({ registrationFull }) => {
  const classes = useStyles()
  return (
    <>
      <Box mb={3}>
        <Box className={classes.grayBox}>
          <Typography
            variant="h6"
            color="textPrimary"
            style={{
              fontFamily: 'PlusJakarta Text',
            }}
          >
            Registration Closed
          </Typography>
        </Box>
      </Box>
      <Typography variant="caption">
        {registrationFull ? (
          <span>Registration for this event has reached capacity.</span>
        ) : (
          <span>The registration window for this event has closed.</span>
        )}
      </Typography>
    </>
  )
}

const RegistrationCard = ({
  program,
  events = [],
  viewNoDates,
  viewAccessCodeRequired,
  viewTeamEligibility,
  viewAnnualMembership,
  eventDates,
}) => {
  const classes = useStyles()
  const cardRef = useRef(null)
  const isScreenSmall = useFitCardToScreen({ cardRef })

  const {
    id,
    coachingFeeInCents,
    collectOnline,
    pgaFeeInCents,
    registrationClose,
    registrationOpen,
    registrationFull,
    registrationStatus,
  } = program

  const noEvents = events.length === 0
  const registrationPeriod = formatDateRange(
    registrationOpen,
    registrationClose,
    { dateFormat: longMonthDayFormat },
  )

  const registrationTitle = getRegistrationTitle(
    eventDates.ended,
    registrationOpen,
  )

  const {
    inRegistrationWindow,
    afterRegistrationOpen,
  } = checkRegistrationWindow(registrationOpen, registrationClose)

  const registrationIsOpen =
    inRegistrationWindow && !eventDates.ended && !registrationFull
  const registerUrl = `${MY_PGA_COM_HOST}/family-golf-registration/${id}`
  const coachFee = coachingFeeInCents / 100
  const pgaFee = pgaFeeInCents / 100
  const totalFee = coachFee + pgaFee

  return (
    <Card
      data-testid="registration-card"
      className={clsx(classes.card, isScreenSmall && classes.smallScreenCard)}
      ref={cardRef}
    >
      <CardContent style={{ padding: '32px' }}>
        <Box mb={2}>
          <Label>{registrationTitle}</Label>
          <Value>{registrationPeriod}</Value>
        </Box>

        <Cost
          viewAnnualMembership={viewAnnualMembership}
          coachFee={coachFee}
          pgaFee={pgaFee}
          totalFee={totalFee}
        />

        {!collectOnline && coachFee > 0 && (
          <OfflinePayment onlineFee={pgaFee} offlineFee={coachFee} />
        )}

        <Box mb={5}>
          <Label>Schedule</Label>
          <Value>{eventDates.dates}</Value>
          {eventDates.time && <Value>{eventDates.time}</Value>}
          {noEvents && (
            <Link onClick={viewNoDates}>Why can't I view event dates?</Link>
          )}
        </Box>

        {registrationIsOpen && (
          <>
            <Box mb={3}>
              {registrationStatus === 'CODE_REQUIRED' ? (
                <RegisterWithAccessCode
                  viewAccessCodeRequired={viewAccessCodeRequired}
                  registerUrl={registerUrl}
                  programId={id}
                />
              ) : (
                <Button
                  component={Link}
                  href={registerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth
                  variant="contained"
                  color="primary"
                  data-testid="registration-button"
                >
                  Register
                </Button>
              )}
            </Box>

            <Box align="left">
              <Typography variant="caption" component="p">
                Must be 18 years or older to register
              </Typography>
              <Typography variant="caption" color="primary">
                <Link onClick={viewTeamEligibility}>
                  View all team eligibility &amp; requirements
                </Link>
              </Typography>
            </Box>
          </>
        )}

        {!registrationIsOpen && afterRegistrationOpen && (
          <RegistrationClosed registrationFull={registrationFull} />
        )}
      </CardContent>
    </Card>
  )
}

export default RegistrationCard
